import React, { useState } from "react";
import { useVisibility, type VisibilityLevel } from "@/contexts/VisibilityContext";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

interface BulkActionsProps {
  pageId: string;
  sections: { id: string; label: string }[];
}

export function BulkActions({ pageId, sections }: BulkActionsProps) {
  const { setVisibility, isPublicMode } = useVisibility();
  const [loading, setLoading] = useState(false);

  const applyToAll = async (visibility: VisibilityLevel) => {
    if (sections.length === 0) return;
    
    setLoading(true);
    try {
      await Promise.all(
        sections.map((s) =>
          setVisibility({
            moduleId: `${pageId}.${s.id}`,
            moduleType: "section",
            visibility,
          })
        )
      );
    } catch (error) {
      console.error("Bulk action failed:", error);
    } finally {
      setLoading(false);
    }
  };

  if (sections.length === 0) return null;

  return (
    <div className="flex items-center gap-2 py-2 px-3 bg-muted/50 rounded-md my-2">
      <span className="text-xs text-muted-foreground shrink-0">
        Set all {sections.length} sections to:
      </span>
      <div className="flex gap-1.5">
        <Button
          size="sm"
          variant="outline"
          onClick={() => applyToAll("public")}
          disabled={loading || isPublicMode}
          className="h-7 text-xs"
        >
          {loading && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
          Public
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => applyToAll("member")}
          disabled={loading || isPublicMode}
          className="h-7 text-xs"
        >
          Member
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => applyToAll("admin")}
          disabled={loading || isPublicMode}
          className="h-7 text-xs"
        >
          Admin
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => applyToAll("hidden")}
          disabled={loading || isPublicMode}
          className="h-7 text-xs"
        >
          Hidden
        </Button>
      </div>
    </div>
  );
}
